import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { ActionIcon, Button, Group, Popover, Skeleton, Stack, Text } from "@mantine/core"
import { IconMessagePlus, IconMessages, IconTrash } from "@tabler/icons-react"
import { useState } from "react"
import { formatRelative, parsePgTimestamp } from "../../lib/format-date"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

/**
 * The chat-session rail: a "New chat" action, then the caller's sessions
 * (newest-first, passed in already ordered), with the active one highlighted and
 * a confirm-before-delete affordance per row. Pure presentation — every action
 * is a prop callback; the delete Popover holds only local open state.
 */
export const SessionsSidebar = ({
  sessions,
  activeSessionId,
  onSelect,
  onNewChat,
  onDelete,
  loading = false,
}: IProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const [confirmingId, setConfirmingId] = useState<string | null>(null)
  // A mount-time snapshot of "now" keeps the relative-time render pure (React
  // Compiler bans calling `Date.now()` during render); a chat rail doesn't need
  // the labels to tick live.
  const [nowMs] = useState<number>(() => Date.now())

  return (
    <div className={classes.sidebar}>
      <Button
        onClick={onNewChat}
        leftSection={<IconMessagePlus size={16} />}
        variant="light"
        color="brand"
        fullWidth
        className={classes.newChat}
        data-testid="ask-new-chat"
      >
        <Trans>New chat</Trans>
      </Button>

      {loading ? (
        <Stack gap="xs" mt="sm">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} height={44} radius="sm" />
          ))}
        </Stack>
      ) : sessions.length === 0 ? (
        <div className={classes.empty}>
          <IconMessages size={26} className={classes.emptyIcon} aria-hidden />
          <Text size="sm" c="dimmed" ta="center">
            <Trans>No chats yet. Ask your first question to start one.</Trans>
          </Text>
        </div>
      ) : (
        <ul className={classes.list}>
          {sessions.map((session) => {
            const then = parsePgTimestamp(session.updatedAt)?.getTime() ?? new Date(session.updatedAt).getTime()
            const relative = Number.isFinite(then) ? formatRelative(i18n, nowMs - then) : ""
            const isActive = session.id === activeSessionId

            return (
              <li key={session.id} className={classes.item} data-active={isActive ? "true" : undefined}>
                <button
                  type="button"
                  className={classes.itemMain}
                  onClick={() => onSelect(session.id)}
                  aria-current={isActive ? "true" : undefined}
                >
                  <span className={classes.title}>{session.title ? session.title : <Trans>Untitled chat</Trans>}</span>
                  <span className={classes.time}>{relative}</span>
                </button>

                <Popover
                  opened={confirmingId === session.id}
                  onChange={(opened) => setConfirmingId(opened ? session.id : null)}
                  position="bottom-end"
                  withArrow
                  shadow="md"
                  width={220}
                  trapFocus
                >
                  <Popover.Target>
                    <ActionIcon
                      variant="subtle"
                      color="danger"
                      size="sm"
                      className={classes.delete}
                      onClick={() => setConfirmingId(session.id)}
                      aria-label={i18n._(msg`Delete chat`)}
                      title={i18n._(msg`Delete chat`)}
                    >
                      <IconTrash size={15} />
                    </ActionIcon>
                  </Popover.Target>
                  <Popover.Dropdown>
                    <Text size="sm" fw={600} mb={4}>
                      <Trans>Delete this chat?</Trans>
                    </Text>
                    <Text size="xs" c="dimmed" mb="sm">
                      <Trans>This permanently removes the conversation and its answers.</Trans>
                    </Text>
                    <Group justify="flex-end" gap="xs">
                      <Button size="xs" variant="default" onClick={() => setConfirmingId(null)}>
                        <Trans>Cancel</Trans>
                      </Button>
                      <Button
                        size="xs"
                        color="danger"
                        onClick={() => {
                          setConfirmingId(null)
                          onDelete(session.id)
                        }}
                      >
                        <Trans>Delete</Trans>
                      </Button>
                    </Group>
                  </Popover.Dropdown>
                </Popover>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
